/*
 * Product specs panel.
 *
 * Tabs follow the WAI-ARIA tabs pattern: click to switch, arrow keys to move,
 * Home/End to jump. Without JavaScript every panel stays visible, so the
 * content is never hidden behind a script that failed to load.
 *
 * The meters animate once the panel scrolls into view, and the description
 * card drops any paragraph or heading that only holds whitespace.
 */

if (!customElements.get('product-specs-tabs')) {
  customElements.define(
    'product-specs-tabs',
    class ProductSpecsTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        if (this.tabs.length < 2) return;

        this.tabs.forEach((tab) => {
          tab.setAttribute('tabindex', tab.getAttribute('aria-selected') === 'true' ? '0' : '-1');
          tab.addEventListener('click', () => this.select(tab));
          tab.addEventListener('keydown', (event) => this.onKeydown(event));
        });

        this.mountIndicator();
      }

      /* A white pill that slides under whichever tab is selected. */
      mountIndicator() {
        this.nav = this.querySelector('[role="tablist"]');
        if (!this.nav) return;

        this.indicator = document.createElement('span');
        this.indicator.className = 'specs-tabs__indicator';
        this.indicator.setAttribute('aria-hidden', 'true');
        this.nav.prepend(this.indicator);
        this.nav.classList.add('has-indicator');

        this.moveIndicator(false);

        if ('ResizeObserver' in window) {
          new ResizeObserver(() => this.moveIndicator(false)).observe(this.nav);
        } else {
          window.addEventListener('resize', () => this.moveIndicator(false));
        }
      }

      moveIndicator(animate) {
        if (!this.indicator) return;
        const active = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
        if (!active) return;

        if (!animate) this.indicator.style.transition = 'none';
        this.indicator.style.width = active.offsetWidth + 'px';
        this.indicator.style.transform = 'translateX(' + active.offsetLeft + 'px)';
        if (!animate) {
          // Flush the jump, then hand control back to the stylesheet transition.
          void this.indicator.offsetWidth;
          this.indicator.style.transition = '';
        }
      }

      panelFor(tab) {
        const id = tab.getAttribute('aria-controls');
        return id ? this.querySelector('#' + CSS.escape(id)) : null;
      }

      select(tab) {
        this.tabs.forEach((item) => {
          const selected = item === tab;
          item.setAttribute('aria-selected', selected ? 'true' : 'false');
          item.setAttribute('tabindex', selected ? '0' : '-1');

          const panel = this.panelFor(item);
          if (panel) panel.hidden = !selected;
        });

        this.moveIndicator(true);
      }

      onKeydown(event) {
        const index = this.tabs.indexOf(event.currentTarget);
        if (index === -1) return;

        let next = null;
        switch (event.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            next = this.tabs[(index + 1) % this.tabs.length];
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            next = this.tabs[(index - 1 + this.tabs.length) % this.tabs.length];
            break;
          case 'Home':
            next = this.tabs[0];
            break;
          case 'End':
            next = this.tabs[this.tabs.length - 1];
            break;
          default:
            return;
        }

        event.preventDefault();
        this.select(next);
        next.focus();
      }
    }
  );
}

(function cleanDescriptions() {
  const MEDIA = 'img, iframe, video, svg, picture, table, ul, ol, hr, a';

  document.querySelectorAll('[data-specs-clean]').forEach((root) => {
    const candidates = Array.from(root.querySelectorAll('p, h1, h2, h3, h4, h5, h6, div, span'));

    // Walk from the deepest nodes up so an empty span is gone before its parent is judged.
    candidates.reverse().forEach((node) => {
      if (node.querySelector(MEDIA)) return;
      const text = node.textContent.replace(/ /g, ' ').trim();
      if (text === '') node.remove();
    });
  });
})();

(function revealMeters() {
  const panels = document.querySelectorAll('.specs-panel');
  if (!panels.length) return;

  if (!('IntersectionObserver' in window)) {
    panels.forEach((panel) => panel.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    },
    { threshold: 0.2 }
  );

  panels.forEach((panel) => observer.observe(panel));
})();
